"use client";

import { CommunityFilterDropdown } from "./CommunityFilterDropdown";
import { DropdownOption } from "./DropdownOption";
import { REGIONS, type RegionCode } from "@/lib/community/constants";
import type {
  PublicCommunityCountry,
  PublicCommunityRegion,
} from "@/lib/community/types";
import type { CountryCode } from "./CountryFilterChips";

function isRegionCode(code: string): code is RegionCode {
  return REGIONS.some((region) => region.code === code);
}

interface BasedInFilterDropdownProps {
  label?: string;
  selectedRegion: RegionCode | null;
  selectedCountry: CountryCode | null;
  regions: PublicCommunityRegion[];
  countries: PublicCommunityCountry[];
  onChange: (
    type: "region" | "country" | null,
    code: RegionCode | CountryCode | null,
  ) => void;
}

export function BasedInFilterDropdown({
  label: labelProp,
  selectedRegion,
  selectedCountry,
  regions,
  countries,
  onChange,
}: BasedInFilterDropdownProps) {
  const active = Boolean(selectedRegion || selectedCountry);
  const selectedName =
    labelProp ??
    (selectedRegion
      ? regions.find((r) => r.code === selectedRegion)?.name ?? selectedRegion
      : selectedCountry
        ? countries.find((c) => c.code === selectedCountry)?.name ??
          selectedCountry
        : null);

  return (
    <CommunityFilterDropdown
      label={selectedName ?? "Based in"}
      active={active}
      align="right"
    >
      <div className="grid grid-cols-1 gap-1 max-h-[360px] overflow-y-auto">
        <DropdownOption
          selected={!selectedRegion && !selectedCountry}
          onClick={() => onChange(null, null)}
        >
          🌍 All locations
        </DropdownOption>

        <div className="px-3 py-1.5 text-xs font-semibold text-muted uppercase tracking-wider">
          Regions
        </div>
        {regions.map((region) => (
          <DropdownOption
            key={region.code}
            selected={selectedRegion === region.code}
            onClick={() => {
              if (isRegionCode(region.code)) {
                onChange("region", region.code);
              }
            }}
          >
            {region.emoji_flag} {region.name}
          </DropdownOption>
        ))}

        <div className="px-3 py-1.5 text-xs font-semibold text-muted uppercase tracking-wider">
          Countries
        </div>
        {countries.map((country) => (
          <DropdownOption
            key={country.code}
            selected={selectedCountry === country.code}
            onClick={() => onChange("country", country.code)}
          >
            {country.emoji_flag} {country.name}
          </DropdownOption>
        ))}
      </div>
    </CommunityFilterDropdown>
  );
}
